export type ValueRow = {
	key: string;
	label: string;
	isAsset?: boolean;

	parentPaths?: string[][];

	canDrill?: boolean;
};

export type ParentSummary =
	| { kind: "path"; path: string }
	| { kind: "many"; count: number; paths: string[] };

const PATH_SEPARATOR = " › ";

export const parentSummary = (row: ValueRow): ParentSummary | null => {
	const paths = (row.parentPaths ?? []).filter((path) => path.length > 0);

	if (paths.length === 0) {
		return null;
	}

	const joined = paths.map((path) => path.join(PATH_SEPARATOR));

	if (joined.length === 1) {
		return { kind: "path", path: joined[0] };
	}

	return { kind: "many", count: joined.length, paths: joined };
};

export const searchableText = (row: ValueRow): string[] => [
	row.label,
	...(row.parentPaths ?? []).flat(),
];

export const soleSelectedKey = (
	rows: ValueRow[],
	isSelected: (row: ValueRow) => boolean
): string | null => {
	let found: string | null = null;

	for (const row of rows) {
		if (!isSelected(row)) {
			continue;
		}

		if (found !== null) {
			return null;
		}

		found = row.key;
	}

	return found;
};

export const rowRange = (
	rows: ValueRow[],
	anchorKey: string | null,
	targetKey: string
): ValueRow[] => {
	if (!anchorKey) {
		return [];
	}

	const anchorIndex = rows.findIndex((row) => row.key === anchorKey);
	const targetIndex = rows.findIndex((row) => row.key === targetKey);

	if (anchorIndex === -1 || targetIndex === -1) {
		return [];
	}

	const [from, to] =
		anchorIndex <= targetIndex
			? [anchorIndex, targetIndex]
			: [targetIndex, anchorIndex];

	return rows.slice(from, to + 1);
};

export const keysAfterSelectAll = (
	selected: Set<string>,
	rows: ValueRow[]
): Set<string> => new Set([...selected, ...rows.map((row) => row.key)]);

export const keysAfterDeselectAll = (
	selected: Set<string>,
	rows: ValueRow[]
): Set<string> => {
	const dropped = new Set(rows.map((row) => row.key));

	return new Set([...selected].filter((key) => !dropped.has(key)));
};

/**
 * Narrows to one row, leaving what the search hides untouched. On the row that
 * survived the button reads "All", and clicking puts the scope back.
 */
export const keysAfterOnly = (
	selected: Set<string>,
	rows: ValueRow[],
	row: ValueRow
): Set<string> => {
	const sole = soleSelectedKey(rows, (candidate) =>
		selected.has(candidate.key)
	);

	if (sole === row.key) {
		return keysAfterSelectAll(selected, rows);
	}

	const onScreen = new Set(rows.map((other) => other.key));

	return new Set([
		...[...selected].filter((key) => !onScreen.has(key)),
		row.key,
	]);
};
